import React, { forwardRef, useState } from "react";
import { COLORS } from "../theme/colors";
import { MATERIALS } from "../theme/materials";

/* ── Button ─────────────────────────────────────────────── */
function getButtonVariant(variant) {
  const variants = {
    primary: {
      background: COLORS.accent,
      color: "#fff",
      border: "none",
    },
    secondary: {
      background: "transparent",
      color: COLORS.textMuted,
      border: `1px solid ${COLORS.border}`,
    },
    danger: {
      background: `${COLORS.pink}10`,
      color: COLORS.pink,
      border: `1px solid ${COLORS.pink}30`,
    },
    ghost: {
      background: "transparent",
      color: COLORS.textMuted,
      border: "none",
    },
    // Glass-shell pill — for showcase surfaces (Library landing toolbar,
    // Marketplace browse). Uses thinGlass material; reads as "translucent
    // chrome floating over the gradient backdrop". DON'T use on dense
    // work surfaces — thickGlass cards expect solid `secondary` chrome
    // for legibility.
    tonal: {
      ...MATERIALS.thinGlass(),
      color: COLORS.text,
      borderRadius: 12,
    },
  };
  return variants[variant] || variants.primary;
}

const BUTTON_SIZES = {
  sm: { padding: "5px 12px", fontSize: 10 },
  md: { padding: "8px 18px", fontSize: 12 },
};

export const Button = forwardRef(({
  variant = "primary", size = "md", loading, disabled, icon,
  fullWidth, children, style, ...rest
}, ref) => {
  const v = getButtonVariant(variant);
  const s = BUTTON_SIZES[size] || BUTTON_SIZES.md;
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      style={{
        ...s,
        ...v,
        borderRadius: 8,
        fontWeight: 600,
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
        transition: "all 0.15s",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        whiteSpace: "nowrap",
        width: fullWidth ? "100%" : undefined,
        ...(loading && variant === "primary" ? { background: COLORS.textDim } : {}),
        ...style,
      }}
      onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.opacity = "0.85"; }}
      onMouseLeave={e => { if (!isDisabled) e.currentTarget.style.opacity = isDisabled ? "0.5" : "1"; }}
      {...rest}
    >
      {icon && <span style={{ fontSize: s.fontSize + 2 }}>{icon}</span>}
      {loading ? "..." : children}
    </button>
  );
});
Button.displayName = "Button";

/* ── IconButton ─────────────────────────────────────────── */
export function IconButton({ icon, size = "md", variant = "default", title, style, ...rest }) {
  const dim = size === "sm" ? 28 : 32;
  return (
    <button
      title={title}
      style={{
        width: dim, height: dim, display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: 6, border: variant === "ghost" ? "none" : `1px solid ${COLORS.border}`,
        background: "transparent", color: COLORS.textMuted,
        cursor: "pointer", fontSize: size === "sm" ? 14 : 16, padding: 0,
        transition: "all 0.12s",
        ...style,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      {...rest}
    >
      {icon}
    </button>
  );
}

/* ── Input ──────────────────────────────────────────────── */
function getBaseInputStyle() {
  return {
    width: "100%", padding: "8px 10px", borderRadius: 7, fontSize: 12,
    background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };
}

const focusHandlers = {
  onFocus: (e) => { e.target.style.borderColor = COLORS.accent + "60"; e.target.style.boxShadow = `0 0 12px ${COLORS.accentGlow}`; },
  onBlur: (e) => { e.target.style.borderColor = COLORS.border; e.target.style.boxShadow = "none"; },
};

export const Input = forwardRef(({ label, helper, style, onFocus, onBlur, ...rest }, ref) => (
  <div>
    {label && <Label>{label}</Label>}
    <input
      ref={ref}
      style={{ ...getBaseInputStyle(), ...style }}
      onFocus={e => { focusHandlers.onFocus(e); onFocus?.(e); }}
      onBlur={e => { focusHandlers.onBlur(e); onBlur?.(e); }}
      {...rest}
    />
    {helper && <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 3 }}>{helper}</div>}
  </div>
));
Input.displayName = "Input";

/* ── TextArea ───────────────────────────────────────────── */
export const TextArea = forwardRef(({ label, helper, style, onFocus, onBlur, rows = 3, ...rest }, ref) => (
  <div>
    {label && <Label>{label}</Label>}
    <textarea
      ref={ref}
      rows={rows}
      style={{ ...getBaseInputStyle(), resize: "vertical", ...style }}
      onFocus={e => { focusHandlers.onFocus(e); onFocus?.(e); }}
      onBlur={e => { focusHandlers.onBlur(e); onBlur?.(e); }}
      {...rest}
    />
    {helper && <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 3 }}>{helper}</div>}
  </div>
));
TextArea.displayName = "TextArea";

/* ── Select ─────────────────────────────────────────────── */
export const Select = forwardRef(({ label, helper, children, style, onFocus, onBlur, ...rest }, ref) => (
  <div>
    {label && <Label>{label}</Label>}
    <select
      ref={ref}
      style={{ ...getBaseInputStyle(), ...style }}
      onFocus={e => { focusHandlers.onFocus(e); onFocus?.(e); }}
      onBlur={e => { focusHandlers.onBlur(e); onBlur?.(e); }}
      {...rest}
    >
      {children}
    </select>
    {helper && <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 3 }}>{helper}</div>}
  </div>
));
Select.displayName = "Select";

/* ── Modal ──────────────────────────────────────────────── */
export function Modal({ open, onClose, title, width = 480, footer, children, zIndex = 1000 }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="panel-scroll"
        style={{
          width, maxWidth: "90vw", maxHeight: "85vh", overflowY: "auto",
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 16, boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
          animation: "fadeIn 0.15s ease-out",
        }}
      >
        {title && (
          <div style={{
            padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>{title}</div>
            <button onClick={onClose} style={{
              background: "transparent", border: "none", color: COLORS.textMuted,
              cursor: "pointer", fontSize: 18, padding: "2px 6px",
            }}>{"\u00D7"}</button>
          </div>
        )}
        <div style={{ padding: "20px 24px" }}>{children}</div>
        {footer && (
          <div style={{
            padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`,
            display: "flex", justifyContent: "flex-end", gap: 8,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Badge ──────────────────────────────────────────────── */
export function Badge({ color = COLORS.accent, children, style, ...rest }) {
  return (
    <span {...rest} style={{
      padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: `${color}15`, color, border: `1px solid ${color}25`,
      textTransform: "capitalize", whiteSpace: "nowrap",
      ...style,
    }}>
      {children}
    </span>
  );
}

/* ── TogglePill ─────────────────────────────────────────── */
//
// Single rounded "pill" button with a clean active/inactive state.
// Use for filter chips, status toggles, channel selectors, category
// pills — anywhere there's a "tap to toggle a single concept" that
// would otherwise become an ad-hoc inline-style button.
//
// active=true        → accent-tinted background + accent-tinted border
// active=false       → transparent + neutral border
// hover (inactive)   → subtle surface tint via inline mouse handlers
//
// Always rendered as <button>; accepts standard button props (onClick,
// title, disabled). Pass children for arbitrary content; pass `count`
// for the trailing-number convention ("Live  8") used on filter pills.
export function TogglePill({
  active = false, onClick, title, disabled, count, leadingIcon,
  size = "md", style, children, ...rest
}) {
  const padding = size === "sm" ? "5px 10px" : "8px 12px";
  const fontSize = size === "sm" ? 11 : 12;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding, fontSize, fontWeight: 600,
        color: active ? COLORS.text : COLORS.textMuted,
        background: active ? `${COLORS.accent}15` : "transparent",
        border: `1px solid ${active ? `${COLORS.accent}55` : COLORS.border}`,
        borderRadius: 999, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (active || disabled) return;
        e.currentTarget.style.background = `${COLORS.surface}AA`;
      }}
      onMouseLeave={(e) => {
        if (active || disabled) return;
        e.currentTarget.style.background = "transparent";
      }}
      {...rest}
    >
      {leadingIcon}
      {children}
      {count != null && (
        <span style={{ fontSize: fontSize - 1, fontWeight: 700, opacity: active ? 1 : 0.7 }}>
          {count}
        </span>
      )}
    </button>
  );
}


/* ── SegmentedControl ───────────────────────────────────── */
//
// 2-N segmented control: glass-shell shell wrapping a row of equal
// segments where exactly one is active. Use for binary/ternary mode
// toggles (Density: Comfy/Compact, View: Grid/List, Theme: Light/Dark/
// System). All segments share identical padding + radius; active gets
// the accent-tint background.
//
//   <SegmentedControl
//     value={density}
//     onChange={setDensity}
//     options={[
//       { value: "comfortable", label: "Comfy" },
//       { value: "compact",     label: "Compact" },
//     ]}
//   />
//
// Each option may carry { value, label, icon, title }. `icon` renders
// instead of `label` when set (icon-only segmented controls).
export function SegmentedControl({
  value, onChange, options, size = "md", style,
}) {
  const segPad = size === "sm" ? "4px 8px" : "6px 12px";
  const segFont = size === "sm" ? 10 : 11;
  return (
    <div style={{
      display: "inline-flex",
      ...MATERIALS.thinGlass(),
      borderRadius: 12,
      padding: 3,
      gap: 2,
      ...style,
    }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active ? "true" : "false"}
            onClick={() => onChange(opt.value)}
            title={opt.title || opt.label}
            style={{
              padding: opt.icon ? "6px 10px" : segPad,
              border: "none",
              borderRadius: 9,
              background: active ? `${COLORS.accent}1F` : "transparent",
              color: active ? COLORS.text : COLORS.textDim,
              fontSize: segFont, fontWeight: 600, letterSpacing: "0.02em",
              cursor: "pointer",
              transition: "background 0.15s, color 0.15s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {opt.icon || opt.label}
          </button>
        );
      })}
    </div>
  );
}


/* ── Card ───────────────────────────────────────────────── */
// I² Visual Identity v1 consistency pass 2026-04-29: every shared Card
// across every tab now floats above the Shell's gradient backdrop via
// spatialShadow. Same surface convention used by Library tiles, Builder
// panels, Admin sub-cards, Detail Viewer cards (which already added
// spatialShadow at R1.4) — the visual language is unified.
export function Card({ padding = "20px 22px", noBorder, children, style, ...rest }) {
  return (
    <div
      style={{
        background: COLORS.surface,
        border: noBorder ? "none" : `1px solid ${COLORS.border}`,
        borderRadius: 16,
        padding,
        boxShadow: MATERIALS.spatialShadow(),
        transition: "border-color 0.2s, transform 0.2s",
        ...style,
      }}
      onMouseEnter={e => { if (!noBorder) e.currentTarget.style.borderColor = COLORS.accent + "40"; }}
      onMouseLeave={e => { if (!noBorder) e.currentTarget.style.borderColor = COLORS.border; }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ── Label ──────────────────────────────────────────────── */
export function Label({ children, style }) {
  return (
    <label style={{
      fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.05em",
      textTransform: "uppercase", marginBottom: 4, display: "block",
      ...style,
    }}>
      {children}
    </label>
  );
}

/* ── Table: Th / Td ─────────────────────────────────────── */
export function Th({ children, style, ...rest }) {
  return (
    <th style={{
      padding: "10px 14px", fontSize: 10, fontWeight: 700,
      color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em",
      textAlign: "left", borderBottom: `1px solid ${COLORS.border}`,
      whiteSpace: "nowrap",
      ...style,
    }} {...rest}>
      {children}
    </th>
  );
}

export function Td({ children, style, ...rest }) {
  return (
    <td style={{
      padding: "10px 14px", fontSize: 12, color: COLORS.text,
      borderBottom: `1px solid ${COLORS.border}06`,
      ...style,
    }} {...rest}>
      {children}
    </td>
  );
}


/** Rich data table with sorting, copy, CSV download, and capped display */
export function RichTable({ el, colors = COLORS }) {
  const C = colors;
  const headers = el.headers || [];
  const displayRows = el.rows || [];
  const allRows = el.all_rows || displayRows;
  const totalRows = el.total_rows || displayRows.length;
  const isCapped = el.capped || false;
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  if (!displayRows.length) return null;

  const sortedRows = React.useMemo(() => {
    if (sortCol === null) return displayRows;
    return [...displayRows].sort((a, b) => {
      const av = (Array.isArray(a) ? a : Object.values(a))[sortCol] || "";
      const bv = (Array.isArray(b) ? b : Object.values(b))[sortCol] || "";
      const an = parseFloat(String(av).replace(/[,$%]/g, ""));
      const bn = parseFloat(String(bv).replace(/[,$%]/g, ""));
      if (!isNaN(an) && !isNaN(bn)) return sortAsc ? an - bn : bn - an;
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [displayRows, sortCol, sortAsc]);

  const handleSort = (idx) => {
    if (sortCol === idx) setSortAsc(!sortAsc);
    else { setSortCol(idx); setSortAsc(true); }
  };

  const toCsv = (rows) => {
    const lines = [headers.join(",")];
    rows.forEach(r => lines.push((Array.isArray(r) ? r : Object.values(r)).map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")));
    return lines.join("\n");
  };
  const handleCopy = () => {
    const text = [headers.join("\t"), ...displayRows.map(r => (Array.isArray(r) ? r : Object.values(r)).join("\t"))].join("\n");
    navigator.clipboard.writeText(text);
  };
  const handleDownload = () => {
    const blob = new Blob([toCsv(displayRows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = (el.title || "data").replace(/[^a-zA-Z0-9]/g, "_") + ".csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const handleDownloadFull = () => {
    const blob = new Blob([toCsv(allRows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = (el.title || "data").replace(/[^a-zA-Z0-9]/g, "_") + "_full.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const btnStyle = { padding: "2px 8px", borderRadius: 5, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontSize: 10, cursor: "pointer" };

  return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}`, marginTop: 6 }}>
      <div style={{ padding: "7px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.bg, flexWrap: "wrap", gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.textDim }}>{el.title || `${totalRows} rows`}</span>
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button onClick={handleCopy} title="Copy to clipboard" style={btnStyle}>{"\uD83D\uDCCB"} Copy</button>
          <button onClick={handleDownload} title="Download CSV" style={btnStyle}>{"\u2B07"} CSV</button>
          {isCapped && <button onClick={handleDownloadFull} title="Download all rows" style={{ ...btnStyle, border: `1px solid ${C.accent || "#6366f1"}40`, color: C.accent || "#6366f1", fontWeight: 600 }}>{"\u2B07"} Full CSV ({totalRows})</button>}
        </span>
      </div>
      <div style={{ maxHeight: 350, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          {headers.length > 0 && (
            <thead><tr style={{ background: C.bg, position: "sticky", top: 0, zIndex: 1 }}>
              {headers.map((h, k) => (
                <th key={k} onClick={() => handleSort(k)} style={{
                  padding: "5px 10px", textAlign: "left", color: C.textDim, fontWeight: 600,
                  borderBottom: `1px solid ${C.border}`, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                }}>
                  {h} <span style={{ fontSize: 8, opacity: 0.5 }}>{sortCol === k ? (sortAsc ? "\u25B2" : "\u25BC") : "\u25BD"}</span>
                </th>
              ))}
            </tr></thead>
          )}
          <tbody>{sortedRows.map((row, k) => (
            <tr key={k} style={{ background: k % 2 === 0 ? "transparent" : C.bg }}>
              {(Array.isArray(row) ? row : Object.values(row)).map((cell, l) => (
                <td key={l} style={{ padding: "4px 10px", color: C.text, borderBottom: `1px solid ${C.border}08` }}>{cell}</td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {isCapped && (
        <div style={{ padding: "6px 12px", background: `${C.orange || "#F59E0B"}08`, borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.orange || "#F59E0B", textAlign: "center" }}>
          Showing {displayRows.length} of {totalRows} rows — click "Full CSV" to download all data
        </div>
      )}
    </div>
  );
}
